import * as React from "react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";

export interface ReportPieChartProps {
  data: Array<{
    name: string;
    value: number;
    color?: string;
  }>;
  dataKey?: string;
  nameKey?: string;
  height?: number;
  innerRadius?: number;
  showLegend?: boolean;
  showPercentages?: boolean;
  showLabels?: boolean;
  className?: string;
  formatValue?: (value: number) => string;
  onSliceClick?: (data: { name: string; value: number; color?: string }, index: number) => void;
}

const COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

export function ReportPieChart({
  data,
  dataKey = "value",
  nameKey = "name",
  height = 350,
  innerRadius = 0,
  showLegend = true,
  showPercentages = true,
  showLabels = false,
  className,
  formatValue,
  onSliceClick,
}: ReportPieChartProps) {
  const chartConfig = React.useMemo(() => {
    const config: ChartConfig = {};
    data.forEach((item, index) => {
      // Get base color from COLORS array or item.color
      const baseColor = item.color || COLORS[index % COLORS.length];
      config[item.name] = {
        label: item.name,
        // Use theme object for light/dark mode colors
        theme: {
          light: baseColor,
          dark: baseColor.replace(")", " / 0.85)"),
        },
      };
    });
    return config;
  }, [data]);

  const total = React.useMemo(() => {
    return data.reduce((sum, item) => sum + item.value, 0);
  }, [data]);

  const renderLabel = (entry: { name: string; value: number }) => {
    if (!showLabels) return null;
    const percentage = ((entry.value / total) * 100).toFixed(1);
    return showPercentages ? `${percentage}%` : formatValue?.(entry.value) || entry.value;
  };

  const customTooltip = ({ active, payload }: {
    active?: boolean;
    payload?: Array<{ name: string; value: number; payload: { name: string; value: number } }>;
  }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0];
    const percentage = ((data.value / total) * 100).toFixed(1);

    return (
      <div className="rounded-lg border bg-background p-2 shadow-sm">
        <div className="grid gap-2">
          <div className="flex flex-col">
            <span className="text-sm font-medium text-muted-foreground">
              {data.name}
            </span>
            <span className="text-sm font-bold">
              {formatValue?.(data.value) || data.value}
            </span>
            {showPercentages && (
              <span className="text-xs text-muted-foreground">
                {percentage}% do total
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ChartContainer config={chartConfig}>
        <PieChart accessibilityLayer>
          <ChartTooltip content={customTooltip} />
          {showLegend && <ChartLegend content={<ChartLegendContent />} />}
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius="80%"
            paddingAngle={2}
            label={renderLabel}
            labelLine={showLabels}
            onClick={onSliceClick ? (data, index) => onSliceClick(data, index) : undefined}
            cursor={onSliceClick ? "pointer" : "default"}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={`var(--color-${entry.name})`}
                style={onSliceClick ? { transition: 'opacity 0.2s' } : undefined}
                onMouseEnter={onSliceClick ? (e: React.MouseEvent) => {
                  e.currentTarget.style.opacity = '0.8';
                } : undefined}
                onMouseLeave={onSliceClick ? (e: React.MouseEvent) => {
                  e.currentTarget.style.opacity = '1';
                } : undefined}
              />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
    </div>
  );
}
